export default `# Facilitator Guidance

The guidance panel provides AI-powered coaching to help you run better sessions. It analyses the conversation in real-time and offers actionable advice.

---

## Phase Goal

At the top, you'll see the current phase objective:
- **Phase name** and goal description
- **Success criteria** — What "done" looks like for this phase (up to 3 items)

Use this as your north star for the current discussion segment.

---

## Session Summary

An AI-generated recap of the conversation so far, highlighting key themes and discussion points. This updates each time you refresh guidance.

---

## Conversation Health

Two key metrics assess discussion quality:

### Balance Score (0–10)
Measures how evenly participation is spread across avatars:
- **7–10** (green) — Healthy, balanced discussion
- **4–6** (amber) — Some voices are dominating
- **1–3** (red) — Significant imbalance

### Engagement Level
Overall conversation intensity: **High**, **Medium**, or **Low**.

### Concerns
If issues are detected, amber alert boxes highlight specific problems:
- One avatar dominating the discussion
- Bias toward a particular viewpoint
- Key topics being overlooked

Each concern includes a suggested intervention.

---

## Missing Perspectives

Identifies avatars that haven't contributed or are underrepresented:
- **Critical** — This avatar's perspective is essential for the current topic
- **Important** — Would add value but isn't blocking
- **Low** — Nice to have

Each entry explains *why* the avatar's perspective matters.

---

## Suggested Prompts

Up to 4 ready-to-use questions you can ask the group. Hover over any prompt and click **Copy** to paste it into your input field.

These are tailored to the current conversation — they target gaps, unexplored angles, or natural next steps.

---

## Recommended Next Step

A single, clear action suggestion at the bottom (highlighted in green). This is the AI's best recommendation for what you should do right now.

---

## Facilitator Chat

On the right side, a dedicated chat lets you have a private conversation with the AI about the session:
- Ask for advice on specific situations
- Get help formulating questions
- Discuss session strategy

This chat is separate from the main session — avatars don't see it.

---

## Tips

- **Refresh guidance at natural breaks** — After a topic wraps up or the phase changes
- **Act on missing perspectives** — If a critical avatar hasn't spoken, directly request their input from the [Avatars](help:session-avatars) panel
- **Use the facilitator chat** for nuanced questions — It has full session context and can give tailored advice
- **Watch the balance score** — A dropping score usually means it's time to redirect the conversation`;
